import { useRef, useState } from "react";
import { Typography } from "antd";
import { SketchPicker } from "react-color";

const { Text } = Typography;

const DEFAULT_PRESET_COLORS = [
  { color: "#d9534f", title: "Red" },
  { color: "#EF7617", title: "Orange" },
  { color: "#FFC83D", title: "Yellow" },
  { color: "#31B452", title: "Green" },
  { color: "#0000FF", title: "Blue" },
  { color: "#000000", title: "Black" },
  { color: "#cccccc", title: "Grey" },
  { color: "#A9A9A9", title: "Dark Grey" },
];

export interface ColorSwatchPickerProps {
  /** Current color; an empty string renders the "Pick a color" placeholder. */
  color: string;
  onChange: (hex: string) => void;
  /** Swatch list shown in the picker. Defaults to the standard 8-color set. */
  presetColors?: { color: string; title: string }[];
  /** Swatch fill + picker seed used when `color` is empty. */
  fallbackColor?: string;
}

/**
 * Color swatch that opens a `SketchPicker` popover, with viewport-aware positioning
 * and a click-outside overlay. Each instance owns its own open state, so it can be
 * rendered any number of times independently. Extracted from the identical inline
 * copies in the pie, table-column, and plot configurators.
 */
export function ColorSwatchPicker({
  color,
  onChange,
  presetColors = DEFAULT_PRESET_COLORS,
  fallbackColor = "#1677ff",
}: ColorSwatchPickerProps) {
  const [open, setOpen] = useState(false);
  const swatchRef = useRef<HTMLDivElement>(null);
  const effective = color || fallbackColor;

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, position: "relative" }}>
      <div
        ref={swatchRef}
        onClick={() => setOpen(!open)}
        style={{
          width: 28,
          height: 28,
          borderRadius: 6,
          border: "1px solid #d9d9d9",
          background: effective,
          cursor: "pointer",
        }}
      />
      <Text style={{ fontSize: 12, color: "#555" }}>
        {color ? color.toUpperCase() : "Pick a color"}
      </Text>
      {open && (
        <div
          style={{
            position: "fixed",
            zIndex: 1050,
            ...((() => {
              const r = swatchRef.current?.getBoundingClientRect();
              if (!r) return { top: 0, left: 0 };
              const pickerH = 310;
              const fitsBelow = r.bottom + 8 + pickerH < window.innerHeight;
              return fitsBelow
                ? { top: r.bottom + 8, left: r.left }
                : { top: r.top - pickerH - 8, left: r.left };
            })()),
          }}
        >
          <div
            style={{ position: "fixed", inset: 0, zIndex: -1 }}
            onClick={() => setOpen(false)}
          />
          <SketchPicker
            color={effective}
            disableAlpha
            presetColors={presetColors}
            onChange={(c: { hex: string }) => onChange(c.hex)}
          />
        </div>
      )}
    </div>
  );
}
